/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import cn from 'classnames';
import moment from 'moment';
import { useSpring, animated as a } from 'react-spring';
import Link from 'next/link';

import HubspotForm from 'react-hubspot-form';

import Button from 'react-scale/components/elements/button';
import getCookie from 'react-scale/lib/util/get-cookie';

import styles from './simple-form.module.css';

const setHiddenUTMFields = () => {
  let utmParams = {};
  try {
    utmParams = JSON.parse(window.localStorage.getItem('utm_params') || '{}');
    Object.keys(utmParams).forEach(property => {
      const utmField = document.querySelector(`input[name=${property}]`);
      if (utmField) {
        // @ts-ignore
        utmField.value = utmParams[property] ?? '';
      }
    });
  } catch (error) {
    // NOTE: ignore error, using localStorage/sessionStorage is disabled in incognito.
  }
};

const ContactInput = ({ form, duplicateId, theme }) => {
  const [formStatus, setFormStatus] = useState('');
  const [message, setMessage] = useState('');
  const [show, setShowCondition] = useState(false);

  const router = useRouter();

  const setupStyle = () => {
    const emailInput = document.querySelector(`#email-${form.id}_${duplicateId}`);

    emailInput.addEventListener('focus', () => {
      setShowCondition(true);
    });
  };

  const onSubmit = async values => {
    // @ts-ignore
    const email = document.querySelector(`#email-${form.id}_${duplicateId}`).value;

    setFormStatus('loading');

    try {
      const hutk = getCookie('hubspotutk');

      const body = {
        submittedAt: moment.utc().valueOf(),
        fields: [...values],
        context: {
          ...(hutk ? { hutk } : undefined),
          pageName: document.querySelector('title').text,
          pageUri: window.location.href
        }
      };

      const bodyStr = JSON.stringify(body);

      await fetch(`https://api.hsforms.com/submissions/v3/integration/submit/4823527/${form.id}`, {
        method: 'POST',
        body: bodyStr,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      setMessage('Redirecting...');
      setFlipped(state => !state);
      localStorage.setItem('email', email);
      router.push('/sales');
    } catch (err) {
      setMessage('Redirecting...');
      setFlipped(state => !state);
      localStorage.setItem('email', email);
      router.push('/sales');
    }
  };

  const hubSpotEventHandler = async event => {
    const { data } = event;
    if (data.type === 'hsFormCallback' && data.eventName === 'onFormSubmit') {
      if (data.id === form.id) {
        await onSubmit(data.data);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('message', hubSpotEventHandler);

    return () => {
      window.removeEventListener('message', hubSpotEventHandler);
    };
  }, []);

  const [flipped, setFlipped] = useState(false);
  const { transform, opacity } = useSpring({
    opacity: flipped ? 1 : 0,
    transform: `perspective(600px) rotateX(${flipped ? 180 : 0}deg)`,
    config: { mass: 5, tension: 500, friction: 80 }
  });

  return (
    <div className="relative items-center block w-full lg:flex simple-inline-form">
      <a.div
        className={cn(styles.will__change, 'absolute inset-0')}
        style={{ opacity, transform: transform.to(t => `${t} rotateX(180deg)`) }}
      >
        {formStatus !== '' && <FormStatus message={message} />}
      </a.div>
      <a.div
        className={cn(styles.will__change, 'w-full')}
        style={{ opacity: opacity.to(o => 1 - o), transform }}
      >
        <div className="flex flex-col w-full md:flex-row md:gap-4">
          <div className="w-full">
            <HubspotForm
              portalId="4823527"
              formId={form.id}
              cssRequired={false}
              formInstanceId={duplicateId} // When embedding the same form on the same page twice, provide this Id for each identical form embed.
              cssClass={styles.simpleForm}
              translations={{
                en: {
                  submitText: form.label
                }
              }}
              submitButtonClass="hidden"
              onReady={() => {
                setupStyle();
                setHiddenUTMFields();
              }}
              loading={<FormStaticLoading />}
            />
          </div>
          <Button
            onClick={() => onSubmit}
            text={form.label}
            theme={theme}
            href={undefined}
            typeButton={undefined}
          />
        </div>
        <p
          className={cn(
            'mobile-spacing text-sm text-silver text-left transition-opacity duration-200 ease-in absolute',
            show
              ? 'opacity-1 select-auto pointer-events-auto'
              : 'opacity-0 select-none pointer-events-none'
          )}
        >
          By proceeding you agree to Scale AI’s{' '}
          <Link href="/legal/terms" className="text-white">
            Privacy Policy
          </Link>
          , and you consent to receive marketing communications.
        </p>
      </a.div>
    </div>
  );
};

const FormStatus = ({ message }) => (
  <div className="absolute flex items-center mb-4">
    <p className="w-full text-base text-transform-gradient sm:whitespace-nowrap">{message}</p>
  </div>
);

const FormStaticLoading = () => (
  <div style={{ display: 'block' }}>
    <form className={styles.simpleForm}>
      <div className="hs_email hs-email hs-fieldtype-text field hs-form-field">
        <div className="input">
          <input
            className="hs-input"
            type="email"
            name="email"
            required
            placeholder="Enter your work email"
          />
        </div>
      </div>
    </form>
  </div>
);

ContactInput.defaultProps = {
  theme: 'dark',
  form: {
    id: '1d163103-bd52-43cb-8fe1-8da65276d3db',
    label: 'Get in Touch'
  },
  duplicateId: 1 // When embedding the same form on the same page twice, provide this Id for each identical form embed.
};

export default ContactInput;
